import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { BackgroundPicker } from "@/components/Backgrounds/BackgroundPicker";
import { EnvValues } from "@/interfaces/customTypes/envValues";
import { BackgroundPolicy } from "@/services/bedrock/dtos/BackgroundAsset";
import { AspectRatio, ContextType, DesignBackground } from "@/services/bedrock/dtos/DesignPackage";
import { getDownloadURLFirebase, saveElementToFirebaseCancellable } from "@/utils/firebase";
import type { BackgroundChoice } from "./ShareFlowProvider";

export interface BackgroundPickerPopoverProps {
  backgrounds: DesignBackground[];
  policy: BackgroundPolicy;
  ratio: AspectRatio;
  contextType: ContextType;
  currentAssetId: number | null;
  currentUrl: string | null;
  isCarousel: boolean;
  uploading: boolean;
  uploadProgress: number | null;
  /** `previewUrl` is the picked background's picture, for showing the choice. */
  onSelect: (choice: BackgroundChoice, previewUrl?: string) => void;
  onUploadFile: (file: File) => void;
}

export const BG_UPLOAD_TOO_LARGE = "That photo couldn't be uploaded. Try a JPG or PNG under 10 MB.";
export const BG_UPLOAD_FAILED = "That photo couldn't be uploaded. Try again.";

// Other formats (HEIC, WebP, GIF, SVG) may not render in the post image.
const UPLOAD_TYPES = ["image/jpeg", "image/png"];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/**
 * Uploads the player's own photo to the web app's bucket (the only place the render accepts it
 * from) and resolves to its download URL. It is used for one post and never enters the library.
 */
export async function uploadPlayerBackground(
  file: File,
  playerId: number,
  onProgress: (pct: number) => void,
  config: EnvValues
): Promise<string> {
  if (!UPLOAD_TYPES.includes(file.type) || file.size > MAX_UPLOAD_BYTES) throw new Error(BG_UPLOAD_TOO_LARGE);
  const node = `player-backgrounds/${playerId}`;
  // The Firebase helper appends the extension from the file type.
  const filename = uuidv4();
  const task = saveElementToFirebaseCancellable(config, node, file, filename);
  task.on("state_changed", snapshot => {
    if (snapshot.totalBytes > 0) onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
  });
  await task;
  return getDownloadURLFirebase(config, node, file, filename);
}

export const BackgroundPickerPopover: React.FC<BackgroundPickerPopoverProps> = ({
  backgrounds,
  policy,
  ratio,
  contextType,
  currentAssetId,
  currentUrl,
  isCarousel,
  uploading,
  uploadProgress,
  onSelect,
  onUploadFile,
}) => {
  const defaultAssetId = backgrounds.find(b => b.isDefault)?.assetId ?? null;
  const selectedAssetId = currentUrl ? null : currentAssetId ?? defaultAssetId;

  return (
    <div className="flex flex-col gap-3" data-testid="background-picker-popover">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-slate-800">Pick a background</span>
        <span className="text-xs text-slate-500">
          {policy === "suggested"
            ? "Your marketing team picked these backgrounds for this design."
            : "Choose a background that fits your market and brand. Curated by your marketing team."}
        </span>
      </div>
      <BackgroundPicker
        mode="player"
        ratio={ratio}
        contextType={contextType}
        suggested={backgrounds}
        selectedAssetId={selectedAssetId}
        searchable={policy !== "suggested"}
        canUpload={policy === "library_and_upload"}
        uploadLabel={uploading ? "Uploading…" : "Upload your own photo"}
        uploading={uploading}
        uploadProgress={uploadProgress}
        onUpload={onUploadFile}
        onSelect={asset => {
          const assetId = "assetId" in asset ? asset.assetId : asset.id;
          onSelect(assetId === defaultAssetId ? null : { assetId }, asset.url);
        }}
        className="max-h-[50vh] overflow-y-auto"
      />
      {isCarousel && <p className="text-xs text-slate-500">This photo is used on every slide.</p>}
    </div>
  );
};
